<template>
	<div id="uppy" class="uppy-modal-theme" style="width: 100%;"></div>
</template>

<script>
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import Dropbox from '@uppy/dropbox';
import GoogleDrive from '@uppy/google-drive';
import AwsS3 from '@uppy/aws-s3';
import _ from 'lodash';
import UserService from '../services/UserService';
import { track, EVENTS } from '@/lib/tracking';
import { UPPY_ALLOWED_VIDEO_FILE_TYPES, MP4_ONLY_ERROR_MESSAGE } from '@/Utils/videoUploadFormats';

import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';

export default {
	props: {
		stream: {
			type: Object,
			required: true,
			validator(v) {
				return Object.prototype.hasOwnProperty.call(v, '_id');
			}
		},
		totalFilesPerStream: {
			type: Number,
			default() { return 999; }
		},
		videoFiles: {
			type: Array,
			default() { return []; }
		},
		uploadFiles: {
			type: Array,
			default() { return []; }
		},
		encodingPresets: {
			type: Array,
			default() { return []; }
		},
		maxFileSize: {
			type: Number,
			default() { return null; }
		},
		storageLimit: {
			type: Object,
			default() { return null; }
		},
		maxTotalFileSize: {
			type: Number,
			default() { return null; }
		},
	},
	data() {
		return {
			uppy: null
		};
	},
	computed: {
		currentFilesNumber() {
			return this.videoFiles.length + this.uploadFiles.length;
		},
		// Sub-folder the import was started from. Companion saves remote imports
		// server-side, so the tag must ride the request URL (guards mirror
		// GlobalUppyUploader.activeSubfolderFor).
		activeSubfolderId() {
			const stream = this.stream;
			if (!stream || stream._playoutDir || stream.type !== 'vod') return null;
			if (this.$route?.params?.streamId !== stream._id) return null;
			return this.$route?.query?.subfolder || null;
		}
	},
	mounted() {
		this.uppy = new Uppy({
			restrictions: {
				maxNumberOfFiles: this.totalFilesPerStream - this.currentFilesNumber <= 0 ? -1 : this.totalFilesPerStream - this.currentFilesNumber,
				minNumberOfFiles: this.totalFilesPerStream - this.currentFilesNumber < 1 ? 0 : 1,
				maxFileSize: this.maxFileSize > 0 ? this.maxFileSize : undefined,
				maxTotalFileSize: this.maxTotalFileSize,
				allowedFileTypes: UPPY_ALLOWED_VIDEO_FILE_TYPES
			},
			locale: {
				strings: {
					youCanOnlyUploadX: {
						0: `You can only upload a maximum of ${this.totalFilesPerStream} files. Upgrade your plan if you need more files.`,
						1: `You can only upload a maximum of ${this.totalFilesPerStream} files. Upgrade your plan if you need more files.`,
					},
					youCanOnlyUploadFileTypes: MP4_ONLY_ERROR_MESSAGE,
					exceedsSize2: 'You have exceeded your storage limit. Please upgrade to continue.'
				}
			},
			autoProceed: false,
			onBeforeFileAdded: (currentFile) => {
				this.uploadFiles.push(currentFile);
				if (currentFile.source === 'Dashboard') {
					this.uppy.info(`Drag'n'Drop not available`, 'error', 5000);
					return false;
				}
				const modifiedFile = { ...currentFile };
				if (modifiedFile.remote) {
					if (modifiedFile.remote.url) {
						modifiedFile.remote.url = modifiedFile.remote.url + `?streamId=${this.stream._id}&streamKey=${this.stream.key}&streamType=${this.stream.type}`;
						if (this.activeSubfolderId) {
							modifiedFile.remote.url += `&subfolder=${encodeURIComponent(this.activeSubfolderId)}`;
						}
					}
					if (modifiedFile.remote.body) {
						modifiedFile.remote.body.encodingPresets = this.encodingPresets;
					}
					// Uppy v5 aws-s3 sends file.meta as the Companion `metadata`; carry the
					// real filename/content-type so Companion records them correctly.
					modifiedFile.meta = { ...(modifiedFile.meta || {}), key: modifiedFile.name, 'content-type': modifiedFile.type || 'video/mp4' };
				}
				this.$emit('upload-enqueue', {upload: modifiedFile, handler: 'uppy'});
				track(EVENTS.VOD_UPLOAD_STARTED, {
					folderId: this.stream._id,
					fileName: modifiedFile.name,
					fileSize: modifiedFile.size,
					format: modifiedFile.type,
					source: 'cloud',
				});
				return modifiedFile;
			},
			onBeforeUpload: (files) => {
				return files;
			}})
			.use(Dashboard, {
				inline: true,
				target: '#uppy',
				theme: 'light',
				height: 380,
				locale: {
					strings: {
						dropHint: '',
						dropPaste: '',
						dropPasteImport: 'Import file from cloud provider'
					}
				}
			})
			.use(Dropbox, {
				target: Dashboard,
				companionUrl: process.env.VUE_APP_COMPANION_API_BASE_URL,
				companionHeaders: {
					'auth-token': UserService.getUserToken()
				}
			})
			// .use(GoogleDrive, {
			// 	target: Dashboard,
			// 	companionUrl: process.env.VUE_APP_COMPANION_API_BASE_URL,
			// 	companionHeaders: {
			// 		'auth-token': UserService.getUserToken()
			// 	}
			// })
			.use(AwsS3, {
				endpoint: process.env.VUE_APP_COMPANION_API_BASE_URL,
				shouldUseMultipart: (file) => file.size > 100 * 2 ** 20
			})
			.on('file-removed', (file, reason) => {
				this.uploadFiles.forEach((item, idx) => {
					if (item.id === file.id) {
						this.uploadFiles.splice(idx, 1);
					}
				});
				this.$emit('upload-dequeue', { upload: file, handler: 'uppy' });
			})
			.on('restriction-failed', (file, reason) => {
				this.uploadFiles.forEach((item, idx) => {
					if (item.id === file.id) {
						this.uploadFiles.splice(idx, 1);
					}
				});
				const reasonStr = reason ? reason.toString() : '';
				if(reasonStr.includes('exceeding your storage')) {
					let errorMessage = 'upload failed'
					if (this.maxFileSize && this.storageLimit && file.data.size > this.maxFileSize && file.data.size < this.storageLimit.unitsLeft) {
						errorMessage = `Your file size cannot exceed ${parseFloat(this.maxFileSize/(1000 ** 3)).toFixed(1)}GB. Upload smaller file or upgrade your plan to continue.`;
					}
					if (this.storageLimit && file.data.size > this.storageLimit.unitsLeft) {
						errorMessage = `Storage capacity cannot exceed ${parseFloat(this.storageLimit.assigned/(1000 ** 3)).toFixed(1)}GB. Upgrade your plan to continue.`;
					}
					this.$emit('filesize-restriction-exceeded', errorMessage);
				} else if (reasonStr.includes(MP4_ONLY_ERROR_MESSAGE) || /you can only upload/i.test(reasonStr)) {
					this.$notify({ group: 'error', text: MP4_ONLY_ERROR_MESSAGE });
				}
			})
			.on('upload-error', (file, error, response) => {
				this.$emit('upload-error', {file, error, response});
			})
			.on('upload-success', (result, response) => {
				const body = (response && response.body) || {};
				const uploadInfo = {
					fileName: result.name,
					fileNameOriginal: result.name,
					bytes: result.size,
					type: result.type,
					source: result.source,
					creationTime: new Date(),
					enabled: true,
					...body,
					mediaInfo: body.mediaInfo || body.mediainfo,
					id: body.id || body.videoId,
				};
				this.uppy.removeFile(result.id);
				this.uploadFiles.forEach((item, idx) => {
					if (item.id === result.id) {
						this.uploadFiles.splice(idx, 1);
					}
				})
				this.uppy.setOptions({
					restrictions: {
						maxNumberOfFiles: this.totalFilesPerStream - this.currentFilesNumber <= 0 ? -1 : this.totalFilesPerStream - this.currentFilesNumber,
						minNumberOfFiles: this.totalFilesPerStream - this.currentFilesNumber < 1 ? 0 : 1,
						maxTotalFileSize: this.maxTotalFileSize - uploadInfo.bytes,
					}
				});
				track(EVENTS.VOD_UPLOADED, {
					folderId: this.stream._id,
					fileId: uploadInfo.id,
					fileSize: uploadInfo.bytes,
					source: 'cloud',
				});
				this.$emit('upload-success', uploadInfo);
			});
	},
	watch: {
		videoFiles() {
			this.$emit('video-files', this.videoFiles);
			this.uppy.setOptions({
				restrictions: {
					maxNumberOfFiles: this.totalFilesPerStream - this.currentFilesNumber <= 0 ? -1 : this.totalFilesPerStream - this.currentFilesNumber,
					minNumberOfFiles: this.totalFilesPerStream - this.currentFilesNumber < 1 ? 0 : 1,
				}
			});
		},
		uploadFiles() {
		}
	}
};
</script>

<style>
div[data-uppy-acquirer-id="MyDevice"] {
    display: none !important;
}
.uppy-modal-theme .uppy-Dashboard-AddFiles-title {
  font-size: var(--font-s-xl);
  color: #fff;
  text-align: left;
  font-weight: 400;
  margin: 0px;
  padding: 15px;
  border-bottom: 1px solid var(--c-dark-3);
}
.uppy-Dashboard-AddFiles-list{
	justify-content: center;
	width: 100%;
}
.uppy-modal-theme .uppy-Dashboard-inner span[role="presentation"] {
  display: flex;
  justify-content: center;
}
.uppy-modal-theme .uppy-DashboardTab {
  flex: 0 0 auto;
  width: 280px;
  max-width: 80%;
  padding: 10px;
	border: none;
}
.uppy-modal-theme .uppy-DashboardTab-btn {
  background-color: var(--c-main-1);
  border: none;
  border-radius: 12px;
  color: #fff;
  width: 100%;
  height: 200px;
	text-align: center;
	flex-direction: column;
	justify-content: center;
}
.uppy-modal-theme .uppy-DashboardTab-btn svg {
	margin-bottom: 10px;
}
.uppy-modal-theme .uppy-c-btn-link {
	padding: 5px 10px;
	background-color: transparent;
	border: 1px solid var(--c-dark-3);
	color: var(--c-dark-8);
	border-radius: 6px;
}
.uppy-modal-theme .uppy-Dashboard-poweredBy {
  display: none;
}
.uppy-modal-theme .uppy-Dashboard-inner {
	background-color: transparent;
	border: none;
  max-height: 100%;
  max-width: 100%;
}
.uppy-modal-theme .uppy-Dashboard-AddFiles {
	border: 0;
	margin: 0;
}
.uppy-modal-theme .uppy-DashboardContent-panel, .uppy-modal-theme .uppy-ProviderBrowser-viewType--list, .uppy-modal-theme .uppy-ProviderBrowser-headerBar, .uppy-modal-theme .uppy-ProviderBrowser-user, .uppy-modal-theme .uppy-ProviderBrowser-list, .uppy-modal-theme .uppy-ProviderBrowser-search, .uppy-modal-theme .uppy-ProviderBrowser-footer, .uppy-modal-theme .uppy-Dashboard-AddFilesPanel, .uppy-modal-theme .uppy-StatusBar:not([aria-hidden=true]).is-waiting, .uppy-modal-theme .uppy-StatusBar.is-waiting .uppy-StatusBar-actions {
	color: #fff;
	background-color: var(--c-dark-2);
	border: none;
}
.uppy-modal-theme .uppy-DashboardContent-bar {
	background-color: var(--c-dark-2);
	border-bottom: none;
}
.uppy-modal-theme .uppy-Provider-breadcrumbs .uppy-u-reset, .uppy-modal-theme .uppy-ProviderBrowser-list .uppy-u-reset {
	background: transparent;
	border: none;
	color: var(--c-dark-8);
}
.uppy-modal-theme .uppy-Dashboard-Item-action--remove{
    background: transparent;
    border: none;
}
.uppy-Dashboard-Item, .uppy-StatusBar-actions{
	border: none;
}
.uppy-StatusBar:before{
	display: none;
}
.uppy-StatusBar-actionCircleBtn {
    background: transparent;
    border: 0px;
}
.uppy-modal-theme .uppy-ProviderBrowser-list .uppy-ProviderBrowserItem-fakeCheckbox {
	background-color: var(--c-dark-3);
}
.uppy-modal-theme .uppy-c-btn-primary {
	background-color: var(--c-main-1);
	border-radius: 6px;
	border: none;
	color: #fff;
	padding: 5px 10px;
}
.uppy-modal-theme .uppy-Provider-authTitle {
	color: var(--c-dark-8);
}
.uppy-modal-theme .uppy-ProviderBrowser-searchInput {
	background-color: var(--c-dark-3);
}
.uppy-modal-theme .uppy-ProviderBrowser-userLogout {
	border: 1px solid var(--c-dark-3);
	background-color: transparent;
	color: #fff;
	padding: 4px 8px;
	float: right;
	position: relative;
	top: -2px;
}
.uppy-Provider-breadcrumbs{
	margin: 0;
}
.uppy-ProviderBrowser-header{
	border: 0px;
}
.uppy-ProviderBrowser-headerBar{
	display: flex;
	align-items: center;
}
</style>


<style scoped>


</style>
